'use client'

import {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'

import { useSheetGesture } from '@/pages/guest/search/lib/gesture'
import { useSheetResize } from '@/pages/guest/search/lib/resize'
import { cn } from '@/shared/lib'

export type BottomSheetHandle = {
  collapse: () => void
  snapToDefaultHeight: () => void
}

export type Props = {
  children: ReactNode
  /** 스크롤 가능한 컨텐츠 영역 ref (scrollTop, touch 타겟) */
  contentRef: RefObject<HTMLElement | null>
  /** 필터 영역 ref. 터치 시작이 여기면 가로 스크롤 시 필터만, 세로 스크롤 시 시트 동작 */
  filterRef?: RefObject<HTMLElement | null>
  /** 접힌 상태에서 보일 높이(px) */
  collapsedHeight?: number
  /** 접힘/펼침 상태 변경 시 호출 (스냅 애니메이션 종료 후에만 호출됨) */
  onCollapsedChange?: (collapsed: boolean) => void
  /** ref 전달 시 .collapse()로 원하는 시점에 접기. 이후 제스처는 내부 상태로 동작 */
  ref?: RefObject<BottomSheetHandle | null>
  defaultCollapsed?: boolean
  defaultCollapsedHeight?: number
  className?: string
  style?: CSSProperties
}

export const BottomSheet = ({
  children,
  contentRef,
  filterRef,
  collapsedHeight: collapsedHeightProp,
  className,
  ref: refProp,
  defaultCollapsed,
  defaultCollapsedHeight,
  onCollapsedChange,
  style,
}: Props) => {
  const sheetRef = useRef<HTMLDivElement>(null)
  const maxSheetYRef = useRef(0)
  const currentSheetYRef = useRef(0)
  // 시트가 초기 기본 위치(완전히 열리지도 닫히지도 않은 상태)에 있는지 여부
  const isAtDefaultPositionRef = useRef(true)

  const [collapsed, setCollapsed] = useState<boolean | null>(null)

  const setAtDefaultPosition = useCallback((value: boolean) => {
    isAtDefaultPositionRef.current = value
    if (sheetRef.current) {
      sheetRef.current.dataset.halfCollapsed = String(value)
    }
  }, [])

  useSheetResize({
    sheetRef,
    collapsedHeightProp,
    defaultCollapsed,
    defaultCollapsedHeight,
    maxSheetYRef,
    currentSheetYRef,
    setAtDefaultPosition,
    setCollapsed,
  })

  const { collapse, snapToDefaultHeight } = useSheetGesture({
    sheetRef,
    contentRef,
    filterRef,
    maxSheetYRef,
    currentSheetYRef,
    defaultCollapsedHeight,
    setAtDefaultPosition,
    setCollapsed,
    onCollapsedChange,
  })

  useImperativeHandle(refProp, () => ({ collapse, snapToDefaultHeight }), [
    collapse,
    snapToDefaultHeight,
  ])

  return (
    <div
      ref={sheetRef}
      data-collapsed={collapsed}
      data-half-collapsed={isAtDefaultPositionRef.current}
      className={cn(
        'group fixed inset-x-0 bottom-12.5 flex flex-col overflow-hidden rounded-t-3xl border-t border-gray-100 bg-white',
        className,
      )}
      style={{
        ...style,
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        visibility: defaultCollapsed ? 'hidden' : 'visible',
      }}
    >
      <div className="mx-auto mt-3 mb-4 h-1 w-[46px] shrink-0 rounded-[10px] bg-gray-100" />
      {children}
    </div>
  )
}
